import { ChangeEvent, useEffect } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Button, FormControl, MenuItem, Pagination, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { IPagination } from '../../interfaces/response';
import { exportToExcel } from '../../utils/file';

interface Filterable {
	[key: string]: any;
}

interface AdminDataGridProps<T extends Filterable> {
	rows: T[];
	columns: GridColDef<T>[];
	pageSize: number;
	loading?: boolean;
	setPageSize: (size: number) => void;
	page: number;
	setPage: (page: number) => void;
	searchTerm?: string;
	actionButtonLable?: string;
	handleSearch?: (event: ChangeEvent<HTMLInputElement>) => void;
	onClick?: () => void;
	searchableFields?: (keyof T)[];
	pagination?: IPagination | null;
	activeItem?: string;
}

const AdminDataGrid = <T extends Filterable>({ pagination, rows, activeItem = '', loading, columns, pageSize, setPageSize, page, setPage, searchTerm, handleSearch, onClick }: AdminDataGridProps<T>) => {
	const { t } = useTranslation();

	const handleExportExcel = () => {
		exportToExcel({ data: rows, fileName: 'user_Profile', t });
	};

	const handleChangePage = (_: ChangeEvent<unknown>, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeSize = (event: SelectChangeEvent) => {
		setPageSize(parseInt(event.target.value));
		setPage(1);
	};

	const totalItems = pagination?.total;
	const currentPageFirstRowNumber = 1 + (page - 1) * pageSize;
	const currentPageLastRowNumber = rows.length + (page - 1) * pageSize;
	const showBtnCreate = activeItem === t('marketers') || activeItem === t('campaigns') || activeItem === t('company_management');
	// Reset page if the current page exceeds the total pages after filtering
	useEffect(() => {
		if (page > Math.ceil(totalItems! / pageSize)) {
			setPage(1);
		}
	}, [searchTerm, page, pageSize, totalItems]);

	return (
		<Box className="w-full overflow-x-hidden rounded-md bg-background-paper p-0 lg:w-auto">
			<>
				<Box className="flex w-full items-center justify-between p-6 lg:w-auto">
					<FormControl className="h-[38px] w-[70px]" size="small">
						<Select id="page-size-select" value={pageSize.toString()} onChange={handleChangeSize}>
							<MenuItem value={10}>10</MenuItem>
							<MenuItem value={25}>25</MenuItem>
							<MenuItem value={50}>50</MenuItem>
							<MenuItem value={100}>100</MenuItem>
						</Select>
					</FormControl>
					<Box className="flex gap-x-4">
						<TextField id="outlined-basic" label={t('search')} variant="outlined" size="small" value={searchTerm} onChange={handleSearch} />
						<Button sx={{ boxShadow: 2 }} className="h-9 bg-primary-main px-5 py-2 text-sm text-background-paper" onClick={handleExportExcel}>
							<span className="pe-1">{t('export_excel')}</span>
						</Button>

						{showBtnCreate && (
							<Button sx={{ boxShadow: 2 }} className="h-9 bg-primary-main px-5 py-2 text-sm text-background-paper" onClick={onClick}>
								<span className="pe-1">{activeItem === t('company_management') ? t('create_new_company') : activeItem === t('campaigns') ? t('create_campaign') : t('create_a_new_marketer')}</span> +
							</Button>
						)}
					</Box>
				</Box>
				<Box className="overflow-x-auto">
					<DataGrid localeText={{ noRowsLabel: t('no_rows') }} loading={loading} autoHeight hideFooter className={`border-x-0 bg-background-paper lg:w-auto lg:min-w-full`} rows={rows} columns={columns} hideFooterPagination disableRowSelectionOnClick disableAutosize disableColumnFilter disableColumnMenu disableColumnResize disableColumnSorting disableColumnSelector />
				</Box>

				{totalItems! > 0 && (
					<Box className={`flex w-full items-center px-6 py-4 lg:w-auto`}>
						{Math.ceil(totalItems! / pageSize) > 1 && <Pagination count={Math.ceil(totalItems! / pageSize)} page={page} onChange={handleChangePage} variant="text" showFirstButton={false} showLastButton={false} className={`lg:min-w-auto flex justify-center`} size="medium" shape="rounded" color="primary" />}
						<div className="grow" />
						<Typography className="text-base tracking-[0.45px] text-text-disabled">
							<Trans i18nKey="data-grid-pagination-label" components={[<span key="0" />, <span key="1" />, <span key="2" />]} values={{ firstRow: currentPageFirstRowNumber, lastRow: currentPageLastRowNumber, total: totalItems }} />
						</Typography>
					</Box>
				)}
			</>
		</Box>
	);
};

export default AdminDataGrid;
